import { FunctionComponent, useCallback, useMemo, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles'
import Box from '@mui/material/Box'
import MuiDrawer from '@mui/material/Drawer'
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import List from '@mui/material/List'
import CssBaseline from '@mui/material/CssBaseline'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'

import {
  //Score,
  AccountTree,
  MapsHomeWork,
  Apartment,
  Router,
  //ReceiptLong,
  Group,
  Logout,
  AccountCircle,
} from '@mui/icons-material'
import MenuIcon from '@mui/icons-material/Menu'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { UseLogout } from '../API'

const drawerWidth = 200

interface InavigationList {
  title: string
  navigation: string
  icon: React.JSX.Element
}

const navigationList: InavigationList[] = [
  /*
  {
    title: 'Novērošana',
    navigation: 'observation',
    icon: <Score />,
  },
*/
  {
    title: 'Telpas',
    navigation: 'roomsList',
    icon: <MapsHomeWork />,
  },
  {
    title: 'Lokācijas',
    navigation: 'location',
    icon: <AccountTree />,
  },
  {
    title: 'Telpas tipi',
    navigation: 'roomTypes',
    icon: <Apartment />,
  },
  {
    title: 'Kontrolieru tipi',
    navigation: 'controllers',
    icon: <Router />,
  },
  /*
  {
    title: 'Maksājumi',
    navigation: 'payments',
    icon: <ReceiptLong />,
  },
*/
  {
    title: 'Lietotāji',
    navigation: 'users',
    icon: <Group />,
  },
]

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
})

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
})

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}))

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}))

const MiniDrawer: FunctionComponent = () => {
  const { t } = useTranslation('')
  const theme = useTheme()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  const selectedNav = useMemo(() => {
    const selected = navigationList.find((navigation) => location.pathname.includes(navigation.navigation))
    if (selected) {
      return selected
    } else {
      return navigationList[0]
    }
  }, [location])

  const handleDrawerOpen = () => {
    setOpen(true)
  }

  const handleDrawerClose = () => {
    setOpen(false)
  }

  const handleMenuClick = (nav: InavigationList) => {
    navigate(nav.navigation)
  }

  const handleLogout = useCallback(async () => {
    const isLogon = await UseLogout()

    if (isLogon) {
      window.location.href = '/'
    }
  }, [])

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position='fixed' open={open}>
        <Toolbar>
          <IconButton
            color='inherit'
            aria-label='open drawer'
            onClick={handleDrawerOpen}
            edge='start'
            sx={{
              marginRight: 5,
              ...(open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant='h6' noWrap component='div'>
            {t(`Telpamon - ${selectedNav.title}`)}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />
          <Box>
            <IconButton
              size='small'
              edge='end'
              onClick={() => {
                navigate('/profile')
              }}
            >
              <AccountCircle />
            </IconButton>
            <IconButton size='small' edge='end' onClick={handleLogout}>
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer variant='permanent' open={open}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {navigationList.map((navigation) => (
            <ListItem key={navigation.navigation} disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                selected={selectedNav.navigation === navigation.navigation}
                onClick={() => handleMenuClick(navigation)}
                title={t(navigation.title)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  {navigation.icon}
                </ListItemIcon>
                <ListItemText primary={t(navigation.title)} sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
      <Box component='main' sx={{ flexGrow: 1, p: 1 }}>
        <DrawerHeader />
        {<Outlet />}
      </Box>
    </Box>
  )
}

export default MiniDrawer
